import { ConfirmationDialog, IconButton, Id, isSuccess, useBulkEdit, useOpen, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { mapToBulkRequestWithFilterId } from "../../../../../shared/api/mappings";
import { contactUpdateInquiryService } from "../../../shared/api/contactUpdateInquiryService";

interface Props {
	contactUpdateInquiryId: Id;
	filterId: Id | null;
}

export const OpenDeletionInquiryBulkAction: FC<Props> = ({ contactUpdateInquiryId, filterId }) => {
	const { t } = useTranslation();
	const { handleClose } = useOpen();
	const { selectedRows } = useBulkEdit();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();
	const [acceptConfirmationOpen, setAcceptConfirmationOpen] = useState(false);
	const [declineConfirmationOpen, setDeclineConfirmationOpen] = useState(false);

	const bulkAcceptDeletionInquiries = useCallback(
		async (acceptInquiry: boolean) => {
			if (acceptInquiry) {
				setAcceptConfirmationOpen(false);
			} else {
				setDeclineConfirmationOpen(false);
			}

			const response = await contactUpdateInquiryService.bulkAcceptDeletionInquiries(contactUpdateInquiryId, {
				...mapToBulkRequestWithFilterId(selectedRows, filterId),
				acceptInquiry,
			});

			if (isSuccess(response)) {
				if (acceptInquiry) {
					showToast({
						body: t("Deletion inquiries were accepted."),
						title: t("Deletion inquiries"),
						type: "success",
					});
				} else {
					showToast({
						body: t("Deletion inquiries were declined."),
						title: t("Deletion inquiries"),
						type: "success",
					});
				}
				await queryClient.invalidateQueries({
					queryKey: contactUpdateInquiryService.bulkAcceptDeletionInquiriesKey(contactUpdateInquiryId),
				});
				await queryClient.invalidateQueries({
					queryKey: contactUpdateInquiryService.detailsKey(contactUpdateInquiryId),
				});
				handleClose();
			}
		},
		[contactUpdateInquiryId, filterId, handleClose, queryClient, selectedRows, showToast, t],
	);

	return (
		<>
			<IconButton
				size="small"
				onClick={() => setAcceptConfirmationOpen(true)}
				disabled={selectedRows.length === 0}
				icon="check"
				title={t("Accept deletion inquiries")}
				color="primary"
			/>
			<IconButton
				size="small"
				onClick={() => setDeclineConfirmationOpen(true)}
				disabled={selectedRows.length === 0}
				icon="close"
				title={t("Decline deletion inquiries")}
				color="secondary"
			/>
			<ConfirmationDialog
				heading={t("Accept deletion inquiries")}
				labels={{ close: t("Close"), confirm: t("Confirm") }}
				open={acceptConfirmationOpen}
				onClose={() => setAcceptConfirmationOpen(false)}
				action={() => bulkAcceptDeletionInquiries(true)}
			>
				{t("Are you sure you want to accept this deletion inquiries.")}
			</ConfirmationDialog>
			<ConfirmationDialog
				heading={t("Decline deletion inquiries")}
				labels={{ close: t("Close"), confirm: t("Confirm") }}
				open={declineConfirmationOpen}
				onClose={() => setDeclineConfirmationOpen(false)}
				action={() => bulkAcceptDeletionInquiries(false)}
			>
				{t("Are you sure you want to decline this deletion inquiries.")}
			</ConfirmationDialog>
		</>
	);
};
